import { API_BASE_URL } from './config';
import { escapeHtml, apiFetch, createModal, renderError, markLoaded } from './helpers';
import type { HockeyGame, HockeyResponse } from './types';

function hockeyPoints(result: string | null): number {
    if (!result) return 0;
    if (result.startsWith('W') && (result.includes('OT') || result.includes('SO'))) return 2;
    if (result.startsWith('W')) return 3;
    if (result.startsWith('L') && (result.includes('OT') || result.includes('SO'))) return 1;
    return 0;
}

function stageLabel(stage: string): string {
    switch (stage) {
        case 'quarterfinal': return 'Puolivälierä';
        case 'semifinal': return 'Välierä';
        case 'final': return 'Finaali';
        case 'bronze': return 'Pronssiottelu';
        default: return '';
    }
}

function getEliminationRiskGames(games: HockeyGame[]): Set<number> {
    const prelim = games.filter(g => (g.stage ?? 'preliminary') === 'preliminary');
    const completed = prelim.filter(g => g.status === 'completed');
    const upcoming = prelim.filter(g => g.status === 'upcoming');
    const currentPoints = completed.reduce((sum, g) => sum + hockeyPoints(g.result), 0);
    const eliminationThreshold = 12;

    const riskIndices = new Set<number>();
    for (let i = 0; i < upcoming.length; i++) {
        const maxAfterLoss = currentPoints + (upcoming.length - i - 1) * 3;
        if (maxAfterLoss < eliminationThreshold) {
            const gameIndex = games.indexOf(upcoming[i]);
            riskIndices.add(gameIndex);
        }
    }
    return riskIndices;
}

function renderHockeyGames(container: HTMLElement, games: HockeyGame[], allGames?: HockeyGame[]): void {
    const eliminationRisks = allGames ? getEliminationRiskGames(allGames) : new Set<number>();
    let html = '';
    for (const game of games) {
        const resultClass = game.result?.startsWith('W') ? 'hockey-win' :
            game.result?.startsWith('L') ? 'hockey-loss' : '';
        const statusBadge = game.status === 'upcoming'
            ? '<span class="hockey-upcoming-badge">Tulossa</span>'
            : '';
        const scoreDisplay = (game.finlandGoals != null && game.opponentGoals != null)
            ? `<span class="hockey-score ${resultClass}">${game.finlandGoals}–${game.opponentGoals}</span>`
            : '<span class="hockey-score hockey-pending">—</span>';

        const timeDisplay = game.time ? `<span class="hockey-time">${escapeHtml(game.time)}</span>` : '';

        const gameIndex = allGames ? allGames.indexOf(game) : -1;
        const eliminationBadge = (game.status === 'upcoming' && eliminationRisks.has(gameIndex))
            ? '<span class="hockey-elimination-badge">⚠ Ratkaiseva</span>'
            : '';

        const stage = game.stage ?? 'preliminary';
        const stageBadge = stage !== 'preliminary'
            ? `<span class="hockey-stage-badge hockey-stage-${stage}">${stageLabel(stage)}</span>`
            : '';
        const opponentText = game.opponent === 'TBD'
            ? '<span class="hockey-tbd">TBD</span>'
            : `${game.isHome ? 'vs' : '@'} ${escapeHtml(game.opponent)}`;

        html += `<div class="hockey-game ${game.status === 'upcoming' ? 'hockey-game-upcoming' : ''} ${stage !== 'preliminary' ? 'hockey-game-playoff' : ''}">
            <span class="hockey-date">${escapeHtml(game.date.replace(/\s*2026$/, ''))}</span>
            ${timeDisplay}
            ${stageBadge}
            <span class="hockey-opponent">${opponentText}</span>
            ${scoreDisplay}${statusBadge}${eliminationBadge}
        </div>`;
    }
    container.innerHTML = html;
}

function openHockeyModal(data: HockeyResponse): void {
    const existing = document.getElementById('hockey-modal');
    if (existing) existing.remove();

    const completed = data.games.filter(g => g.status === 'completed');
    const upcoming = data.games.filter(g => g.status === 'upcoming');
    const wins = completed.filter(g => g.result?.startsWith('W')).length;
    const losses = completed.filter(g => g.result?.startsWith('L')).length;
    const draws = completed.filter(g => g.result?.startsWith('D')).length;
    const goalsFor = completed.reduce((s, g) => s + (g.finlandGoals ?? 0), 0);
    const goalsAgainst = completed.reduce((s, g) => s + (g.opponentGoals ?? 0), 0);

    let summary = '';
    if (completed.length === 0) {
        summary = 'Turnaus ei ole vielä alkanut.';
    } else {
        const totalGames = data.games.length;
        const played = completed.length;
        const remaining = upcoming.length;
        const goalDiff = goalsFor - goalsAgainst;
        const diffStr = goalDiff > 0 ? `+${goalDiff}` : `${goalDiff}`;
        const winRate = Math.round((wins / played) * 100);
        summary = `${played}/${totalGames} ottelua pelattu`;
        if (remaining > 0) summary += ` · ${remaining} jäljellä`;
        summary += ` · Voitto-% ${winRate}% · Maaliero ${diffStr}`;
    }

    const modal = createModal('hockey-modal', 'hockey-modal-overlay', `
        <div class="hockey-modal">
            <div class="hockey-modal-header">
                <div>
                    <span class="hockey-modal-title">🇫🇮 Suomi — ${escapeHtml(data.tournament)}</span>
                    <div class="hockey-modal-subtitle">Lohko ${escapeHtml(data.group)} · ${escapeHtml(data.location)}</div>
                </div>
                <button class="hockey-modal-close" data-modal-close aria-label="Close">&#10005;</button>
            </div>
            <div class="hockey-modal-stats">
                <span class="hockey-stat">${wins}V</span>
                <span class="hockey-stat">${losses}H</span>
                ${draws ? `<span class="hockey-stat">${draws}T</span>` : ''}
                <span class="hockey-stat-sep">·</span>
                <span class="hockey-stat">${goalsFor}–${goalsAgainst} maalit</span>
            </div>
            <div class="hockey-modal-summary">${escapeHtml(summary)}</div>
            <div class="hockey-modal-body"></div>
        </div>`);

    const body = modal.querySelector('.hockey-modal-body') as HTMLElement;
    renderHockeyGames(body, data.games, data.games);
}

export async function fetchHockey(): Promise<void> {
    const container = document.getElementById('hockey-games');
    if (!container) return;

    try {
        const response = await apiFetch(`${API_BASE_URL}/hockey`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data: HockeyResponse = await response.json();

        if (!data.games || data.games.length === 0) {
            container.innerHTML = '<p class="hockey-empty">No games available.</p>';
            return;
        }

        const upcoming = data.games.filter(g => g.status === 'upcoming');
        const preview = upcoming.slice(0, 4);

        renderHockeyGames(container, preview, data.games);

        const today = new Date();
        const todayDay = today.getDate();
        const todayMonth = today.toLocaleString('en-US', { month: 'long' });
        const todayYear = today.getFullYear();
        const todayGame = upcoming.find(g => {
            const m = g.date.match(/^(\d{1,2})\s+(\w+)\s+(\d{4})$/);
            if (!m) return false;
            return parseInt(m[1]) === todayDay && m[2] === todayMonth && parseInt(m[3]) === todayYear;
        });
        if (todayGame) {
            const timeStr = todayGame.time ? ` klo ${todayGame.time}` : '';
            container.insertAdjacentHTML('afterbegin',
                `<div class="hockey-today"><span class="hockey-live-pulse"></span> Ottelu tänään${timeStr} — ${todayGame.isHome ? 'vs' : '@'} ${escapeHtml(todayGame.opponent)}</div>`);
        }

        if (preview.length === 0 && !todayGame) {
            container.innerHTML = '<p class="hockey-empty">Ei tulevia otteluita.</p>';
        }

        const widget = container.closest('.hockey-widget');
        if (widget) {
            widget.classList.add('hockey-clickable');
            widget.addEventListener('click', (e: Event) => {
                const target = e.target as HTMLElement;
                if (target.closest('a')) return;
                openHockeyModal(data);
            });
        }

        markLoaded(container, 'hockey');
    } catch (error) {
        renderError(container, 'hockey', 'Could not load hockey data.', error);
    }
}
